import { Order, ORDER_STATUS, SERVICE_TYPE } from '@baemingo/order-status-events/dist/types';
import styled from '@emotion/styled';
import React, { useMemo } from 'react';
import { useStorage } from '~Components/Storage';
import { useTableUniverse } from '~Components/TableUniverse';
import { useI18n, useSelector } from '~Hooks';
import { GetTable_table } from '~Scenes/EventedKitchenDisplay/Queries/__queries__';
import { Box } from '~Styles';
import { formatOrderType } from '~utilities';
import { statusBackgroundColor } from '..';
import PeopleIcon from '~Assets/icons/people_alt.svg';

const Wrapper = styled(Box)`
  cursor: pointer;
`;

const HeaderText = styled.div<{
  styleMultiplier: {
    space: number;
  };
}>`
  flex: 1;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 0px calc(16px - 0.2em * ${({ styleMultiplier }) => styleMultiplier?.space});
`;

const OrderType = styled.span<{
  styleMultiplier: {
    size: number;
  };
}>`
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  text-align: center;
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
`;

const HeaderCourseBadge = styled.div<{
  color?: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  padding: 6px 10px;
  border-radius: 999px;
  background: ${({ color }) => color || '#dedede'};
  color: var(--colorBlack);
  font-size: 14px;
  line-height: 1;
  white-space: nowrap;
  font-weight: 500;
`;

/** Wraps the people icon and the coverage count (number of guests on the table). */
const Coverage = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  margin-left: 8px;
  margin-right: 8px;
  color: var(--colorWhite);
`;

/** Coverage count, sized in line with the order type text. */
const CoverageCount = styled.span<{
  styleMultiplier: {
    size: number;
  };
}>`
  color: var(--colorWhite);
  font-size: calc(20px - 0.1em * ${({ styleMultiplier }) => styleMultiplier?.size});
`;

/** People icon masked the same way as the take away icon, used to indicate coverage. */
const CoverageIcon = styled.div`
  width: 24px;
  height: 24px;
  background-color: var(--colorWhite);
  margin-left: 8px;
  mask-image: url(${PeopleIcon});
`;

interface Props {
  children?: React.ReactNode;
  table: GetTable_table | null;
  order: Order;
  orderTitle: string;
  setTouching: (value: React.SetStateAction<boolean>) => void;
  handleHeadTouchEnd: () => Promise<void>;
}

const HeaderInfo = React.memo(({
  children,
  table,
  order,
  orderTitle,
  setTouching,
  handleHeadTouchEnd,
}: Props) => {
  const { i18n } = useI18n();
  const [storage] = useStorage();

  const courseTypes = useSelector((state) => state.courseTypes);
  const tableUniverse = useTableUniverse();

  const orderCourseType = useMemo(() => {
    if (!order.courseId || !courseTypes || courseTypes.length === 0) {
      return null;
    }

    return courseTypes.find((c) => c.id === order.courseId) || null;
  }, [order.courseId, courseTypes]);

  const coverage = useMemo(() => {
    if (!table?.id) {
      return null;
    }

    const cheque = tableUniverse.cheques.find((c) => c.tableId === table?.id);

    return cheque?.coverage ?? null;
  }, [table?.id, tableUniverse.cheques]);

  return (
    <Wrapper
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      onTouchStart={() => {
        if (
          !storage.disableOrderProgress ||
          (order.origin === 'APP' && order.serviceType === SERVICE_TYPE.TAKE_AWAY)
        ) {
          setTouching(true);
        }
      }}
      onTouchEnd={handleHeadTouchEnd}
      style={{
        backgroundColor: statusBackgroundColor(order.status as ORDER_STATUS),
      }}>
      <Box
        display="flex"
        alignItems="center"
        flexDirection="row"
        justifyContent="space-between"
        fluid>
        <HeaderText styleMultiplier={storage.styleMultiplier}>
          <OrderType styleMultiplier={storage.styleMultiplier}>
            {storage.KDSHideOrderHeader ? (
              orderTitle
            ) : table?.code ? (
              <span>
                {i18n('KDS.Table')} <big>#{table.code}</big>
              </span>
            ) : (
              formatOrderType(order.serviceType || SERVICE_TYPE.EAT_HERE, i18n)
            )}
          </OrderType>
          {orderCourseType && (
            <HeaderCourseBadge color={orderCourseType.color}>
              <span>{i18n(orderCourseType.name)}</span>
            </HeaderCourseBadge>
          )}
        </HeaderText>

        {coverage ? (
          <Coverage>
            <CoverageCount styleMultiplier={storage.styleMultiplier}>{coverage}</CoverageCount>
            <CoverageIcon />
          </Coverage>
        ) : null}

        {children}
      </Box>
    </Wrapper>
  );
});

HeaderInfo.displayName = 'HeaderInfo';

export default HeaderInfo;
